import { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldOff, CheckCircle2, XCircle, Copy, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Pagina self-service per l'autenticazione a due fattori (TOTP) del proprio account — a
// differenza di Users, che gestisce gli altri utenti, questa opera sempre e solo sull'utente
// loggato (nessun parametro id, tutte le route usano authenticateToken + req.user.userId).
export default function SecurityPage() {
  const { user } = useAuth();
  const [step, setStep] = useState<'idle' | 'setup' | 'backup-codes'>('idle');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(!!user?.totpEnabled);

  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  // Audit 23/10/2026 (A8): il backend chiede la password solo per sostituire un 2FA già attivo
  // (flusso non offerto da questa pagina: prima si disattiva con password, poi si riattiva).
  const [setupPassword, setSetupPassword] = useState('');

  async function startSetup() {
    setError(null);
    setBusy(true);
    try {
      const res = await axios.post('/api/auth/2fa/setup', { password: setupPassword });
      setSetupPassword('');
      setQrDataUrl(res.data.data.qrDataUrl);
      setManualSecret(res.data.data.secret);
      setStep('setup');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not start 2FA setup');
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await axios.post('/api/auth/2fa/enable', { code });
      setBackupCodes(res.data.data.backupCodes);
      setStep('backup-codes');
      setTotpEnabled(true);
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await axios.post('/api/auth/2fa/disable', { password: disablePassword });
      setTotpEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      setStep('idle');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Incorrect password');
    } finally {
      setBusy(false);
    }
  }

  function finishSetup() {
    setStep('idle');
    setBackupCodes([]);
  }

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join('\n')).catch(() => { /* clipboard non disponibile, l'utente le copia a mano */ });
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Security</h1>
        <p className="text-sm text-gray-500">Manage two-factor authentication for your own account, {user?.email}.</p>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {step === 'backup-codes' ? (
        <div className="card p-5">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <AlertTriangle size={16} />
            <h2 className="font-medium text-sm">Save your backup codes</h2>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Each code can be used once to sign in if you lose access to your authenticator app. They are
            shown only this once — the platform does not store them in a readable form.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
            {backupCodes.map(c => <span key={c}>{c}</span>)}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyBackupCodes} className="btn-secondary flex items-center gap-1.5">
              <Copy size={13} /> Copy codes
            </button>
            <button onClick={finishSetup} className="btn-primary ml-auto">I've saved them</button>
          </div>
        </div>
      ) : step === 'setup' ? (
        <form onSubmit={confirmEnable} className="card p-5">
          <h2 className="font-medium text-sm text-gray-900 mb-3">Scan the QR code</h2>
          <p className="text-xs text-gray-500 mb-3">
            Open your authenticator app (Google Authenticator, Authy, 1Password, …) and scan this code,
            or enter the key manually.
          </p>
          <div className="flex justify-center mb-3">
            {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR code" className="w-44 h-44 border border-gray-100 rounded-lg" />}
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2 mb-4 text-center">
            <p className="text-[11px] text-gray-400 mb-0.5">Manual entry key</p>
            <p className="font-mono text-xs tracking-wider text-gray-800 break-all">{manualSecret}</p>
          </div>
          <label className="block text-xs text-gray-500 mb-1">Enter the 6-digit code to confirm</label>
          <input
            type="text" inputMode="numeric" autoFocus value={code} onChange={e => setCode(e.target.value)}
            placeholder="000000" required
            className="input-field mb-4 text-center tracking-widest font-mono w-40"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setStep('idle')} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary">
              {busy ? 'Verifying…' : 'Enable 2FA'}
            </button>
          </div>
        </form>
      ) : (
        <div className="card p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {totpEnabled
                ? <ShieldCheck size={22} className="text-emerald-600 mt-0.5" />
                : <ShieldOff size={22} className="text-gray-400 mt-0.5" />}
              <div>
                <h2 className="font-medium text-sm text-gray-900">
                  Two-factor authentication{' '}
                  <span className={`badge ml-1 ${totpEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {totpEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </h2>
                <p className="text-xs text-gray-500 mt-1 max-w-sm">
                  {totpEnabled
                    ? 'A 6-digit code from your authenticator app is required at every sign-in, in addition to your password.'
                    : 'Add an authenticator app as a second factor, so a stolen password alone is not enough to sign in.'}
                </p>
              </div>
            </div>
            {!totpEnabled && (
              <button onClick={startSetup} disabled={busy} className="btn-primary whitespace-nowrap">
                {busy ? 'Starting…' : 'Enable 2FA'}
              </button>
            )}
          </div>

          {totpEnabled && !showDisable && (
            <button onClick={() => setShowDisable(true)} className="mt-4 text-sm text-red-600 hover:underline">
              Disable two-factor authentication
            </button>
          )}

          {totpEnabled && showDisable && (
            <form onSubmit={confirmDisable} className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-xs text-gray-500 mb-1">Confirm your password to disable 2FA</label>
              <div className="flex items-center gap-2">
                <input
                  type="password" autoFocus value={disablePassword} onChange={e => setDisablePassword(e.target.value)}
                  required className="input-field w-56"
                />
                <button type="submit" disabled={busy} className="inline-flex items-center gap-1 text-red-600 hover:bg-red-50 rounded-lg px-3 py-2 text-sm">
                  <XCircle size={14} /> Disable
                </button>
                <button type="button" onClick={() => { setShowDisable(false); setDisablePassword(''); }} className="text-sm text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {totpEnabled && (
            <p className="mt-4 text-xs text-gray-400 inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-500" /> Lost your device and your backup codes? An admin can reset your 2FA from the Users page — you'll need to sign in with your password and set it up again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
